// app/components/SanityImage.tsx (Create this new file)
import React from 'react';
import urlBuilder from '@sanity/image-url';
import { getImageDimensions, SanityImageSource } from '@sanity/asset-utils';

// Import your project details (adjust path as needed)
import { dataset, projectId } from '#app/utils/sanity/projectDetails';

// Import a Zod-derived type for better type safety (adjust path/name)
import type { z } from 'zod';
import type { zImage } from '#app/types/schema.ts'; // Assuming zImage is defined here

// Define the props for the component
// Extends standard img attributes, omitting ones we handle specifically
type SanityImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'width' | 'height'> & {
    image: z.infer<typeof zImage> | null | undefined;
    alt?: string; // Optional override for alt text
    className?: string; // Allow passing custom classes
    width?: number; // Optional: Specify image width for URL builder and img attribute
    height?: number; // Optional: Specify image height for URL builder and img attribute
    quality?: number; // Optional: Sanity image quality (1-100)
    fit?: 'clip' | 'crop' | 'fill' | 'fillmax' | 'max' | 'scale' | 'min'; // Optional fit mode
    sizes?: string; // Optional: Pass sizes attribute for responsive images
    loading?: 'lazy' | 'eager'; // Override default lazy loading
    aspectRatio?: string;
    disableNaturalAspectRatio?: boolean;
};

// Initialize the builder once
const builder = urlBuilder({ projectId, dataset });

export default function SanityImage({
    image,
    alt,
    className,
    width: explicitWidth, // Use explicit prop names
    height: explicitHeight,
    quality,
    fit = 'max', // Default fit
    loading = 'lazy', // Default loading
    sizes, // Pass sizes through
    style,
    aspectRatio: explicitAspectRatio,
    disableNaturalAspectRatio = false,
    ...rest // Pass any other valid img props
}: SanityImageProps) {

    if (!image?.asset) {
        // Return null or a placeholder if image data/asset is missing
        return null;
    }

    // Determine alt text: use prop override -> Sanity alt -> empty fallback
    const altText = alt ?? image.alt ?? '';

    let naturalAspectRatio: string | undefined = undefined;
    // Calculate natural aspect ratio only if not disabled and no explicit ratio provided
    if (!disableNaturalAspectRatio && !explicitAspectRatio) {
        try {
            const dimensions = getImageDimensions(image);
            if (dimensions.width && dimensions.height) {
                naturalAspectRatio = `${dimensions.width} / ${dimensions.height}`;
            }
        } catch (error) { /* console.warn */ }
    }

    // Determine final aspect ratio for inline style: explicit prop > natural (if enabled) > undefined
    const finalAspectRatioForStyle = !disableNaturalAspectRatio
        ? (explicitAspectRatio ?? naturalAspectRatio)
        : undefined; // Don't set inline style if disabled

    // Build the Sanity image URL
    let imageUrlBuilder = builder
        .image(image) // Pass the full image object for hotspot/crop
        .auto('format') // Automatically use webp/avif if supported
        .fit(fit);

    if (explicitWidth) imageUrlBuilder = imageUrlBuilder.width(explicitWidth);
    if (explicitHeight) imageUrlBuilder = imageUrlBuilder.height(explicitHeight);
    if (quality) imageUrlBuilder = imageUrlBuilder.quality(quality);

    const src = imageUrlBuilder.url();

    if (!src) {
        // Handle cases where URL generation fails
        return <div>Error loading image.</div>;
    }

    // TODO: Consider adding srcSet generation using imageUrlBuilder.width(...).url() for multiple sizes

    return (
        <img
            src={src}
            alt={altText}
            className={className} // Apply passed class names
            loading={loading}
            width={explicitWidth ?? getImageDimensions(image)?.width} // Set attribute if known
            height={explicitHeight ?? getImageDimensions(image)?.height} // Set attribute if known
            sizes={sizes} // Pass sizes attribute for responsiveness
            style={{
                ...(finalAspectRatioForStyle && { aspectRatio: finalAspectRatioForStyle }), // Helps prevent layout shift
                display: 'block', // Default display
                ...style, // Allow overriding/adding styles
            }}
            {...rest} // Spread remaining props
        />
    );
}